import * as React from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import BooksStep, {
  StepItems,
} from '../../../../../components/books/model1/BooksStep';
import * as I from '../../../../../interfaces/merchants/resources/books';

import * as Actions from '../../../../../redux/actions/merchants/resources/books';
import BookingWithPageDetail from '../common/BookingWithPageDetail';
import InputBookingEventDateTime from '../InputBookingEventDateTime';
import InputCompletionEvent from '../InputCompletionEvent';
import InputCompletionFailed from '../InputCompletionFailed';
import InputConfirmationEvent from '../InputConfirmationEvent';
import InputPayment from '../InputPayment';
import InputRequireLogin from '../InputRequireLogin';
import InputUserInfo from '../InputUserInfo';

interface StateProps {
  merchantResource: I.MerchantResource;
  publicId: string;
  step: number;
  settings: I.SettingsModelTypeEvent;
  postable: boolean;
  renew?: boolean;
}

interface DispatchProps {
  actions: {
    goTo: (step: number) => () => void;
    onSubmit: (step: number) => () => void;
    onFinalSubmit: (step: number) => () => void;
  };
}

type Props = StateProps & DispatchProps;

interface State {
  renew?: boolean;
}

const FULL_WORKFLOW_STEPS = [
  StepItems.SelectDate,
  StepItems.Customer,
  StepItems.Payment,
  StepItems.Confirmation,
];

const SKIP_PAYMENT_WORKFLOW_STEPS = [
  StepItems.SelectDate,
  StepItems.Customer,
  StepItems.Confirmation,
];
const ERROR_REINPUT_PAYMENT_STEP = 97;
const ERROR_RESTART_STEP = 98;
const ERROR_RESELECT_STEP = 99;

class ModelTypeEvent extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { renew: false };
    this.handleBackToModifyPage = this.handleBackToModifyPage.bind(this);
  }

  public handleBackToModifyPage(step: number) {
    this.setState({ renew: true });
    this.props.actions.goTo(step)();
  }

  private renderRequireLoginAt(step: number) {
    return (
      <InputRequireLogin
        postable={this.props.postable}
        onSubmit={this.props.actions.onSubmit(step)}
        onRestart={this.props.actions.goTo(ERROR_RESTART_STEP)}
        renew={this.props.renew}
      />
    );
  }

  private renderInputBookingEvent(step: number) {
    return (
      <BookingWithPageDetail {...this.props.merchantResource}>
        <InputBookingEventDateTime
          modelType={this.props.settings.modelType}
          settings={this.props.settings.event}
          postable={this.props.postable}
          renew={this.props.renew || this.state.renew}
          onSubmit={this.props.actions.onSubmit(step)}
          onRestart={this.props.actions.goTo(ERROR_RESTART_STEP)}
          withExpressFlow={true}
        />
      </BookingWithPageDetail>
    );
  }

  private renderInputUserInfoAt(step: number, submitLabelIntlId: string) {
    return (
      <InputUserInfo
        modelType={this.props.settings.modelType}
        settings={this.props.settings.userInfo}
        postable={this.props.postable}
        onSubmit={this.props.actions.onSubmit(step)}
        onRestart={this.props.actions.goTo(ERROR_RESTART_STEP)}
        submitLabelIntlId={submitLabelIntlId}
      />
    );
  }

  private renderInputPaymentAt(step: number) {
    return (
      <InputPayment
        modelType={this.props.settings.modelType}
        settings={this.props.settings.payment!}
        postable={this.props.postable}
        onSubmit={this.props.actions.onSubmit(step)}
        onRestart={this.props.actions.goTo(ERROR_RESTART_STEP)}
      />
    );
  }

  private renderInputConfirmationAt(step: number) {
    return (
      <InputConfirmationEvent
        modelType={this.props.settings.modelType}
        merchantResource={this.props.merchantResource}
        settings={this.props.settings.confirmation}
        postable={this.props.postable}
        onSubmit={this.props.actions.onFinalSubmit(step)}
        onReinputPayment={this.props.actions.goTo(ERROR_REINPUT_PAYMENT_STEP)}
        onReselect={this.props.actions.goTo(ERROR_RESELECT_STEP)}
        onRestart={this.props.actions.goTo(ERROR_RESTART_STEP)}
        publicId={this.props.publicId}
      />
    );
  }

  private renderInputCompletion() {
    return (
      <InputCompletionEvent
        merchantResource={this.props.merchantResource}
        settings={this.props.settings.completion!}
        postable={this.props.postable}
        publicId={this.props.publicId}
        withExpressFlow={true}
      />
    );
  }

  private renderFailedRestart() {
    return (
      <InputCompletionFailed
        merchantResource={this.props.merchantResource}
        settings={this.props.settings.completion!}
        backToLabel={'books.submitLabel.restart'}
        backToModifyPage={() => location.reload(false)}
      />
    );
  }

  private renderFailedReselect(modifierStep: number) {
    return (
      <InputCompletionFailed
        merchantResource={this.props.merchantResource}
        settings={this.props.settings.completion!}
        backToLabel={'books.submitLabel.reselect'}
        backToModifyPage={this.props.actions.goTo(modifierStep)}
      />
    );
  }

  private renderFailedReinput(modifierStep: number) {
    return (
      <InputCompletionFailed
        merchantResource={this.props.merchantResource}
        settings={this.props.settings.completion!}
        backToLabel={'books.submitLabel.reinput'}
        backToModifyPage={this.props.actions.goTo(modifierStep)}
      />
    );
  }

  private fullWorkFlowSetting() {
    const { step } = this.props;

    // prettier-ignore
    const setting = {
      0: { displayProgress: false, currentProgress: null,                   renderFn: () => this.renderRequireLoginAt(0)},
      1: { displayProgress: true,  currentProgress: StepItems.SelectDate,   renderFn: () => this.renderInputBookingEvent(1) },
      2: { displayProgress: true,  currentProgress: StepItems.Customer,     renderFn: () => this.renderInputUserInfoAt(2, 'books.submitLabel.payment') },
      3: { displayProgress: true,  currentProgress: StepItems.Payment,      renderFn: () => this.renderInputPaymentAt(3)         },
      4: { displayProgress: true,  currentProgress: StepItems.Confirmation, renderFn: () => this.renderInputConfirmationAt(4)    },
      5: { displayProgress: false, currentProgress: null,                   renderFn: () => this.renderInputCompletion()         },

      [ERROR_REINPUT_PAYMENT_STEP]: { displayProgress: true, currentProgress: StepItems.Confirmation, renderFn: () => this.renderFailedReinput(3) },
      [ERROR_RESTART_STEP]: { displayProgress: true, currentProgress: StepItems.Confirmation, renderFn: () => this.renderFailedRestart() },
      [ERROR_RESELECT_STEP]: { displayProgress: true, currentProgress: StepItems.Confirmation, renderFn: () => this.renderFailedReselect(2) },
    }[step];

    return [FULL_WORKFLOW_STEPS, setting];
  }

  private skipPaymentFlowSetting() {
    const { step } = this.props;

    // prettier-ignore
    const setting = {
      0: { displayProgress: false, currentProgress: null,                   renderFn: () => this.renderRequireLoginAt(0)},
      1: { displayProgress: true,  currentProgress: StepItems.SelectDate,   renderFn: () => this.renderInputBookingEvent(1) },
      2: { displayProgress: true,  currentProgress: StepItems.Customer,     renderFn: () => this.renderInputUserInfoAt(2, 'books.submitLabel.confirm') },
      3: { displayProgress: true,  currentProgress: StepItems.Confirmation, renderFn: () => this.renderInputConfirmationAt(3)    },
      4: { displayProgress: false, currentProgress: null,                   renderFn: () => this.renderInputCompletion()         },

      [ERROR_RESTART_STEP]: { displayProgress: true, currentProgress: StepItems.Confirmation, renderFn: () => this.renderFailedRestart() },
      [ERROR_RESELECT_STEP]: { displayProgress: true, currentProgress: StepItems.Confirmation, renderFn: () => this.renderFailedReselect(2) },
    }[step];

    return [SKIP_PAYMENT_WORKFLOW_STEPS, setting];
  }

  public render() {
    const [allSteps, setting] = this.props.merchantResource.paymentStep
      ? this.fullWorkFlowSetting()
      : this.skipPaymentFlowSetting();
    return (
      <>
        {setting.displayProgress && (
          <BooksStep
            allSteps={allSteps}
            current={allSteps.indexOf(setting.currentProgress) + 1}
          />
        )}
        {setting.renderFn()}
      </>
    );
  }
}

const mapStateProps = ({
  merchantsResourcesBooks,
}: {
  merchantsResourcesBooks: I.State;
}): StateProps => ({
  merchantResource: merchantsResourcesBooks.merchantResource!,
  settings: merchantsResourcesBooks.settings as I.SettingsModelTypeEvent,
  postable: merchantsResourcesBooks.postable!,
  step: merchantsResourcesBooks.step!,
  publicId: merchantsResourcesBooks.merchantId!,
  renew: merchantsResourcesBooks.renew,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  actions: {
    goTo: (step: number) => () => Actions.goTo(step)(dispatch),
    onSubmit: (step: number) => () => Actions.nextStep(step)(dispatch),
    onFinalSubmit: (step: number) => () => Actions.finalStep(step)(dispatch),
  },
});

export default connect(mapStateProps, mapDispatchToProps)(ModelTypeEvent);
